/**
 * TDS on purchases.
 *
 * A deduction is wrong in two directions and both are expensive: deduct too
 * little and the expense is disallowed under section 40(a)(ia) at 30%; deduct
 * where no liability exists and you have taken the vendor's money.
 *
 * Thresholds are what make this non-trivial. Most sections apply only once
 * payments to that vendor cross a limit in the financial year, and 194C has two
 * limits at once — per contract and aggregate. So the decision needs the
 * vendor's year-to-date history, not just the bill in front of you.
 */

import { round2 } from "../sales/tax";
import { actVersionFor, cite, paymentCodeFor, type ActVersion, type Citation, type DeducteeCategory, type PaymentCode } from "../tax/act-versions";
import type { ExpenseNature } from "./types";

export interface TdsSection {
  section: string;
  description: string;
  /** Rate for a company or firm. */
  rateCompany: number;
  /** Rate for an individual or HUF, where it differs. */
  rateIndividual: number;
  /** Threshold per transaction, if any. */
  singleThreshold?: number;
  /** Aggregate threshold for the financial year. */
  annualThreshold?: number;
  natures: ExpenseNature[];
  note?: string;
}

export const TDS_SECTIONS: TdsSection[] = [
  {
    section: "194C",
    description: "Payments to contractors and sub-contractors",
    rateCompany: 2,
    rateIndividual: 1,
    singleThreshold: 30_000,
    annualThreshold: 100_000,
    natures: ["contractor-services", "works-contract"],
    note: "Deduct once a single payment exceeds 30,000 or the aggregate for the year exceeds 1,00,000. A valid declaration from a transporter owning ten or fewer goods carriages exempts the payment under 194C(6)."
  },
  {
    section: "194J",
    description: "Professional or technical services, royalty, non-compete fees",
    rateCompany: 10,
    rateIndividual: 10,
    annualThreshold: 30_000,
    natures: ["professional-services", "legal-services"],
    note: "Technical services and call-centre payments attract 2% rather than 10%."
  },
  {
    section: "194I(a)",
    description: "Rent of plant, machinery or equipment",
    rateCompany: 2,
    rateIndividual: 2,
    annualThreshold: 240_000,
    natures: [],
    note: "Threshold applies per payee for the financial year."
  },
  {
    section: "194I(b)",
    description: "Rent of land, building or furniture",
    rateCompany: 10,
    rateIndividual: 10,
    annualThreshold: 240_000,
    natures: ["rent"]
  },
  {
    section: "194H",
    description: "Commission or brokerage",
    rateCompany: 5,
    rateIndividual: 5,
    annualThreshold: 15_000,
    natures: ["commission"]
  },
  {
    section: "194Q",
    description: "Purchase of goods",
    rateCompany: 0.1,
    rateIndividual: 0.1,
    annualThreshold: 5_000_000,
    natures: ["goods-for-resale", "raw-material"],
    note: "Applies where the buyer's turnover exceeded 10 crore in the preceding year. Deduct on the value exceeding 50 lakh per seller. Takes precedence over the seller's TCS under 206C(1H)."
  },
  {
    section: "194A",
    description: "Interest other than on securities",
    rateCompany: 10,
    rateIndividual: 10,
    annualThreshold: 40_000,
    natures: [],
    note: "Threshold is 50,000 for a senior citizen and 5,000 for interest from a non-banking payer."
  },
  {
    section: "194",
    description: "Dividend",
    rateCompany: 10,
    rateIndividual: 10,
    annualThreshold: 5_000,
    natures: []
  },
  {
    section: "195",
    description: "Payments to a non-resident",
    rateCompany: 20,
    rateIndividual: 20,
    natures: ["import-of-services"],
    note: "Rate depends on the nature of income and any treaty benefit. Form 15CA/15CB is required before remittance."
  }
];

export const SECTION_INDEX = new Map(TDS_SECTIONS.map((section) => [section.section, section]));

/** Suggests the section from the nature of the expense. */
export const sectionForNature = (nature: ExpenseNature | undefined): TdsSection | undefined =>
  nature ? TDS_SECTIONS.find((section) => section.natures.includes(nature)) : undefined;

export type DeducteeType = "company" | "firm" | "individual" | "huf" | "non-resident";

export interface TdsContext {
  /** Payments already made to this vendor under this section this year. */
  yearToDateUnderSection: number;
  deducteeType: DeducteeType;
  /** Without a PAN, section 206AA forces 20% or the section rate, whichever is higher. */
  hasPan: boolean;
  /** A lower or nil deduction certificate under section 197. */
  lowerDeductionRate?: number;
  certificateNumber?: string;
  /** Transporter declaration under 194C(6). */
  transporterDeclaration?: boolean;
  /** Buyer turnover test for 194Q. */
  buyerTurnoverExceeds10Cr?: boolean;
  /**
   * Date of credit and date of payment. The governing Act is decided by
   * whichever is earlier, so both are needed to cite correctly across the
   * 1 April 2026 cutover.
   */
  creditDate?: string;
  paymentDate?: string;
}

export interface TdsAssessment {
  applicable: boolean;
  /** Legacy 1961 Act section, retained as the internal key. */
  section?: string;
  rate: number;
  /** Amount on which the deduction is computed — taxable value, excluding GST. */
  baseAmount: number;
  tdsAmount: number;
  reasons: string[];
  /** True when this bill is the one that crosses the threshold, so earlier
   *  payments in the year must also be deducted upon now. */
  thresholdCrossedNow: boolean;
  /** Catch-up deduction on earlier payments that went undeducted. */
  catchUpAmount: number;
  /** Which Act governs, decided by the earlier of credit or payment. */
  actVersion?: ActVersion;
  /** The citation to display and to carry into the return. */
  citation?: Citation;
  /**
   * Numeric payment code for challans and returns under the 2025 Act.
   * Undefined under the 1961 Act, where the section is cited instead.
   */
  paymentCode?: PaymentCode;
}

/**
 * Works out the deduction for one bill.
 *
 * The base excludes GST where the tax is shown separately on the invoice — a
 * CBDT circular position that people routinely get wrong by deducting on the
 * gross.
 */
/**
 * Attaches the governing Act, the citation and the payment code to an
 * assessment. Applied on every return path so a deduction can never leave this
 * function without knowing which law it was computed under.
 */
const withCitation = (
  assessment: TdsAssessment,
  context: Pick<TdsContext, "creditDate" | "paymentDate" | "deducteeType">
): TdsAssessment => {
  if (!assessment.section) return assessment;
  const creditDate = context.creditDate ?? new Date().toISOString().slice(0, 10);
  const version = actVersionFor(creditDate, context.paymentDate);
  const citation = cite(assessment.section, creditDate, context.paymentDate);
  // The payment code splits by deductee type for some sections, so the same
  // classification that drives the rate also drives the code.
  const category: DeducteeCategory =
    context.deducteeType === "individual" || context.deducteeType === "huf" ? "individual-huf" : "other";
  const paymentCode = paymentCodeFor(assessment.section, creditDate, context.paymentDate, category);
  return {
    ...assessment,
    actVersion: version,
    citation,
    paymentCode,
    reasons: version === "2025"
      ? [
          ...assessment.reasons,
          `Governed by ${citation.display}.${paymentCode ? ` Challans and returns carry payment code ${paymentCode.code}.` : ""}`
        ]
      : assessment.reasons
  };
};

export const assessTds = (
  input: {
    section?: string;
    nature?: ExpenseNature;
    /** Taxable value of the bill, excluding GST. */
    taxableValue: number;
    /** Single largest payment under this bill, for the 194C per-contract test. */
    singlePaymentValue?: number;
  },
  context: TdsContext
): TdsAssessment => {
  const reasons: string[] = [];
  const definition = input.section ? SECTION_INDEX.get(input.section) : sectionForNature(input.nature);

  if (!definition) {
    return { applicable: false, rate: 0, baseAmount: 0, tdsAmount: 0, reasons: ["No TDS section applies to this expense."], thresholdCrossedNow: false, catchUpAmount: 0 };
  }

  // 194C(6): a transporter furnishing a valid declaration is exempt.
  if (definition.section === "194C" && context.transporterDeclaration) {
    return withCitation({
      applicable: false,
      section: definition.section,
      rate: 0,
      baseAmount: input.taxableValue,
      tdsAmount: 0,
      reasons: ["Transporter has furnished a declaration under section 194C(6) of the 1961 Act, carried forward into the 2025 Act, so no deduction is required. The PAN must still be reported in the return."],
      thresholdCrossedNow: false,
      catchUpAmount: 0
    }, context);
  }

  // 194Q applies only where the buyer's own turnover crossed 10 crore.
  if (definition.section === "194Q" && context.buyerTurnoverExceeds10Cr === false) {
    return withCitation({
      applicable: false,
      section: definition.section,
      rate: 0,
      baseAmount: input.taxableValue,
      tdsAmount: 0,
      reasons: ["Deduction on purchase of goods applies only where the buyer's turnover exceeded 10 crore in the preceding year."],
      thresholdCrossedNow: false,
      catchUpAmount: 0
    }, context);
  }

  const aggregateAfter = round2(context.yearToDateUnderSection + input.taxableValue);
  const singleValue = input.singlePaymentValue ?? input.taxableValue;

  const singleBreached = definition.singleThreshold !== undefined && singleValue > definition.singleThreshold;
  const annualBreached = definition.annualThreshold !== undefined && aggregateAfter > definition.annualThreshold;
  const noThresholds = definition.singleThreshold === undefined && definition.annualThreshold === undefined;

  if (!singleBreached && !annualBreached && !noThresholds) {
    return withCitation({
      applicable: false,
      section: definition.section,
      rate: 0,
      baseAmount: input.taxableValue,
      tdsAmount: 0,
      reasons: [
        `Within the ${definition.section} threshold. Aggregate for the year is ${aggregateAfter.toLocaleString("en-IN")}${definition.annualThreshold ? ` against a limit of ${definition.annualThreshold.toLocaleString("en-IN")}` : ""}.`
      ],
      thresholdCrossedNow: false,
      catchUpAmount: 0
    }, context);
  }

  // Base rate by deductee type.
  let rate = context.deducteeType === "individual" || context.deducteeType === "huf"
    ? definition.rateIndividual
    : definition.rateCompany;

  if (context.lowerDeductionRate !== undefined) {
    rate = context.lowerDeductionRate;
    reasons.push(`Lower deduction at ${rate}% under a section 197 certificate${context.certificateNumber ? ` (${context.certificateNumber})` : ""}.`);
  } else if (!context.hasPan) {
    // Section 206AA: the higher of 20% and the section rate.
    const forced = Math.max(20, rate);
    reasons.push(`No PAN on record. Section 206AA requires deduction at ${forced}% instead of ${rate}%.`);
    rate = forced;
  }

  // 194Q deducts only on the value above the threshold, not on the whole bill.
  let baseAmount = input.taxableValue;
  if (definition.section === "194Q" && definition.annualThreshold !== undefined) {
    const alreadyAboveThreshold = Math.max(0, context.yearToDateUnderSection - definition.annualThreshold);
    const liable = Math.max(0, aggregateAfter - definition.annualThreshold - alreadyAboveThreshold);
    baseAmount = round2(Math.min(input.taxableValue, liable));
    reasons.push(`Section 194Q applies only to the value above the ${definition.annualThreshold.toLocaleString("en-IN")} threshold.`);
  }

  // When the annual threshold is crossed by this bill, earlier payments in the
  // year that escaped deduction must be deducted upon now.
  const crossedNow =
    definition.annualThreshold !== undefined &&
    context.yearToDateUnderSection <= definition.annualThreshold &&
    aggregateAfter > definition.annualThreshold &&
    definition.section !== "194Q";
  const catchUpAmount = crossedNow ? round2((context.yearToDateUnderSection * rate) / 100) : 0;
  if (crossedNow && catchUpAmount > 0) {
    reasons.push(`This bill crosses the ${definition.section} annual threshold. Deduct on the earlier ${context.yearToDateUnderSection.toLocaleString("en-IN")} paid this year as well.`);
  }

  if (singleBreached && !annualBreached) {
    reasons.push(`Single payment exceeds the ${definition.singleThreshold!.toLocaleString("en-IN")} limit under ${definition.section}.`);
  }

  const tdsAmount = round2((baseAmount * rate) / 100);
  if (!reasons.length) reasons.push(`${definition.section} applies at ${rate}%.`);
  reasons.push("Deduction is computed on the taxable value; GST shown separately on the invoice is excluded.");

  return withCitation({
    applicable: true,
    section: definition.section,
    rate,
    baseAmount,
    tdsAmount,
    reasons,
    thresholdCrossedNow: crossedNow,
    catchUpAmount
  }, context);
};

/** Due date for depositing TDS — the 7th of the following month, 30 April for March. */
export const tdsDepositDueDate = (deductionDate: string): string => {
  const parsed = new Date(deductionDate);
  const month = parsed.getUTCMonth();
  const year = parsed.getUTCFullYear();
  if (month === 2) return `${year}-04-30`; // March deduction
  const next = new Date(Date.UTC(year, month + 1, 7));
  return next.toISOString().slice(0, 10);
};

/** Quarter and return due date for Form 26Q. */
export const tdsReturnQuarter = (deductionDate: string): { quarter: string; dueDate: string } => {
  const parsed = new Date(deductionDate);
  const month = parsed.getUTCMonth();
  const year = parsed.getUTCFullYear();
  if (month >= 3 && month <= 5) return { quarter: "Q1", dueDate: `${year}-07-31` };
  if (month >= 6 && month <= 8) return { quarter: "Q2", dueDate: `${year}-10-31` };
  if (month >= 9 && month <= 11) return { quarter: "Q3", dueDate: `${year + 1}-01-31` };
  return { quarter: "Q4", dueDate: `${month <= 2 ? year : year + 1}-05-31` };
};

/** Interest for late deduction (1%) or late deposit (1.5%) per month or part. */
export const tdsInterest = (
  amount: number,
  kind: "late-deduction" | "late-deposit",
  fromDate: string,
  toDate: string
): { months: number; rate: number; interest: number } => {
  const from = new Date(fromDate);
  const to = new Date(toDate);
  const months = Math.max(
    0,
    (to.getUTCFullYear() - from.getUTCFullYear()) * 12 + (to.getUTCMonth() - from.getUTCMonth()) + (to.getUTCDate() >= from.getUTCDate() ? 1 : 0)
  );
  const rate = kind === "late-deduction" ? 1 : 1.5;
  return { months, rate, interest: round2((amount * rate * months) / 100) };
};
